/* eslint-disable react/require-default-props */
import React from 'react';

import { DEFAULT_COLORS, DEFAULT_STYLING } from './constants';
import type { ChartDimensions, YScaleResult } from './types';

interface YAxisRendererProps {
  yScaleResult: YScaleResult;
  dimensions: ChartDimensions;
  className?: string;
}

/**
 * YAxisRenderer Component
 *
 * Renders Y-axis labels with proper Typography styling (body2: 12px, 500 weight).
 * Uses design system color tokens for consistency.
 */
function YAxisRenderer({
  yScaleResult,
  dimensions,
  className,
}: YAxisRendererProps) {
  const allTicks = Array.from({ length: yScaleResult.tickCount }).map(
    (_, i) => ({
      index: i,
      value: i * yScaleResult.tickInterval,
      y:
        dimensions.chartBottom -
        yScaleResult.scale(i * yScaleResult.tickInterval),
    })
  );

  // Filter ticks to avoid label overlap (minimum 28px spacing for label height of 24px + 4px margin)
  const MIN_LABEL_SPACING = 28;
  const ticks = allTicks.filter((tick, idx) => {
    if (idx === 0) {
      return true; // Always show first tick
    }

    if (idx === allTicks.length - 1) {
      return true; // Always show last tick
    }

    const prevTick = allTicks[idx - 1];
    const spacing = Math.abs(tick.y - prevTick.y);

    return spacing >= MIN_LABEL_SPACING;
  });

  return (
    <g className={`YAxisRenderer ${className ?? ''}`} role='presentation'>
      {/* Y-axis labels with Typography body2 styling (12px, 500 weight) */}
      <g
        className='YAxisRenderer-labels'
        role='presentation'
        aria-hidden='true'>
        {ticks.map((tick) => {
          // Format label with M suffix for millions
          const formatLabel = (value: number) => {
            if (value === 0) {
              return '0';
            }

            if (value >= 1000000) {
              return `${(value / 1000000).toFixed(0)}M`;
            }

            if (value >= 1000) {
              return `${(value / 1000).toFixed(0)}K`;
            }

            return value.toString();
          };

          return (
            <text
              key={`y-label-${tick.index}`}
              x={dimensions.chartLeft - 12}
              y={tick.y}
              textAnchor='end'
              dominantBaseline='middle'
              className='YAxisRenderer-label'
              fontSize={DEFAULT_STYLING.axisLabelFontSize}
              fontWeight={DEFAULT_STYLING.axisLabelFontWeight}
              fill={DEFAULT_COLORS.axisLabel}>
              {formatLabel(tick.value)}
            </text>
          );
        })}
      </g>

      {/* Y-axis baseline (visual reference) - HIDDEN */}
      <line
        x1={dimensions.chartLeft}
        y1={dimensions.chartTop}
        x2={dimensions.chartLeft}
        y2={dimensions.chartBottom}
        className='YAxisRenderer-baseline'
        stroke={DEFAULT_COLORS.axisLine}
        strokeWidth='1'
        vectorEffect='non-scaling-stroke'
        role='presentation'
        aria-hidden='true'
        style={{ display: 'none' }}
      />
    </g>
  );
}

export default React.memo(YAxisRenderer);
